/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from '@erpsquad/common/runtime';
import { Box, Divider } from "@mui/material";
import Typography from "./typography/typography";
import _ from "lodash";

type PermissionAction = "canEdit" | "canView" | "canViewById" | "canAdd" | "canDelete";

interface SingleModulePermission {
  module: string;
  action: PermissionAction;
}

interface MultiModulePermission {
  modules: string[];
  action: PermissionAction;
  operator?: "AND" | "OR"; // Default: "OR"
}

interface ComplexPermission {
  permissions: Array<{
    module: string;
    action: PermissionAction;
  }>;
  operator?: "AND" | "OR"; // Default: "AND"
}

interface ProtectedRouteProps {
  // Single module (backward compatibility)
  module?: string;
  action?: PermissionAction;
  
  // Multiple modules with same action
  multiModule?: MultiModulePermission;
  
  // Complex permissions with different actions
  complexPermissions?: ComplexPermission;
  
  // Custom permission checker function
  customPermissionCheck?: (permissions: any) => boolean;
  
  // Redirect path for unauthorized access
  redirectTo?: string;
  
  // Show custom error component instead of redirect
  showError?: boolean;
  
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  module,
  action,
  multiModule,
  complexPermissions,
  customPermissionCheck,
  redirectTo = "/403",
  showError = true,
  children
}) => {
  const permissions = usePermissions();

  // Memoize permission check for performance
  const hasPermission = useMemo(() => {
    if (!permissions) {
      return false;
    }

    // Custom permission check takes precedence
    if (customPermissionCheck) {
      return customPermissionCheck(permissions);
    }

    // Complex permissions check
    if (complexPermissions) {
      const { permissions: permList, operator = "AND" } = complexPermissions;
      
      const results = permList.map(({ module: mod, action: act }) => {
        const modulePerms = _.get(permissions, mod, {});
        return modulePerms[act] ?? false;
      });

      return operator === "AND" 
        ? results.every(Boolean)
        : results.some(Boolean);
    }

    // Multi-module with same action check
    if (multiModule) {
      const { modules, action: act, operator = "OR" } = multiModule;
      
      const results = modules.map(mod => {
        const modulePerms = _.get(permissions, mod, {});
        return modulePerms[act] ?? false;
      });

      return operator === "AND" 
        ? results.every(Boolean)
        : results.some(Boolean);
    }

    // Single module check (backward compatibility)
    if (module && action) {
      const modulePerms = _.get(permissions, module, {});
      return modulePerms[action] ?? false;
    }

    // No permission config provided - deny access
    return false;
  }, [permissions, module, action, multiModule, complexPermissions, customPermissionCheck]);

  // Enhanced error component
  const ErrorComponent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)", // Fixed transform values
        gap: 2,
        textAlign: "center",
        p: 3
      }}
    >
      <Typography 
        type="h1" 
        color="theme.secondary.900" 
        weight="bold"
        sx={{ fontSize: { xs: '4rem', sm: '6rem' } }}
      >
        403
      </Typography>
      <Divider 
        flexItem 
        // sx={{ 
        //   borderColor: '#898f9a',
        //   width: '100%',
        //   maxWidth: '200px'
        // }}
      />
      <Typography 
        type="s3" 
        color="theme.secondary.900" 
        weight="medium"
      >
        Access Denied
      </Typography>
      <Typography 
        type="body2" 
        color="theme.secondary.700" 
        weight="regular"
        sx={{ mt: 1, maxWidth: '300px' }}
      >
        You don't have the required permissions to access this resource.
      </Typography>
    </Box>
  );

  // Handle loading state - permissions not yet available or still empty (e.g. right after login)
  if (!permissions || Object.keys(permissions).length === 0) {
    return null; // Wait silently until permissions are loaded
  }

  // Render based on permission check
  if (hasPermission) {
    return <>{children}</>;
  }

  return showError ? <ErrorComponent /> : <Navigate to={redirectTo} replace />;
};

export default ProtectedRoute;

// Helper function to create permission configurations
export const createPermissionConfig = {
  // Single module
  single: (module: string, action: PermissionAction) => ({
    module,
    action
  }),

  // Multiple modules with same action
  multiModule: (
    modules: string[], 
    action: PermissionAction, 
    operator: "AND" | "OR" = "OR"
  ) => ({
    multiModule: { modules, action, operator }
  }),

  // Complex permissions
  complex: (
    permissions: Array<{ module: string; action: PermissionAction }>,
    operator: "AND" | "OR" = "AND"
  ) => ({
    complexPermissions: { permissions, operator }
  }),

  // Custom function
  custom: (checkFn: (permissions: any) => boolean) => ({
    customPermissionCheck: checkFn
  })
};

// Example usage types for better developer experience
export type PermissionConfig = 
  | ReturnType<typeof createPermissionConfig.single>
  | ReturnType<typeof createPermissionConfig.multiModule>
  | ReturnType<typeof createPermissionConfig.complex>
  | ReturnType<typeof createPermissionConfig.custom>;